import { z } from 'zod';

export const DUPLICATE_SIGNALS = {
  guardian_email: { weight: 95, label: 'Same parent/guardian email' },
  child_name_grade_cycle: { weight: 80, label: 'Same child name, grade and cycle' },
  child_name_school: { weight: 78, label: 'Same child name and current school' },
  family_guardian_name: { weight: 60, label: 'Same family name and parent/guardian name' },
  child_name_similar: { weight: 42, label: 'Similar child name, same grade and cycle' },
  phone: { weight: 20, label: 'Same phone number' },
} as const;

export const DUPLICATE_SIGNAL_CODES = Object.keys(DUPLICATE_SIGNALS) as [
  keyof typeof DUPLICATE_SIGNALS,
  ...(keyof typeof DUPLICATE_SIGNALS)[],
];
export const DuplicateSignalCode = z.enum(DUPLICATE_SIGNAL_CODES);
export type DuplicateSignalCode = z.infer<typeof DuplicateSignalCode>;

export const DuplicateSignal = z.object({ code: DuplicateSignalCode, weight: z.number().int() });
export type DuplicateSignal = z.infer<typeof DuplicateSignal>;

export const MAX_DUPLICATE_SCORE = 100;
export const FUZZY_NAME_MIN_SIMILARITY = 0.82;

export const DUPLICATE_LEVELS = ['possible', 'moderate', 'strong', 'very_strong'] as const;
export const DuplicateLevel = z.enum(DUPLICATE_LEVELS);
export type DuplicateLevel = z.infer<typeof DuplicateLevel>;

export const DUPLICATE_LEVEL_LABELS: Record<DuplicateLevel, string> = {
  possible: 'Possible',
  moderate: 'Moderate',
  strong: 'Strong',
  very_strong: 'Very strong',
};

const threshold = z
  .number()
  .int('Whole number')
  .min(1, 'At least 1')
  .max(MAX_DUPLICATE_SCORE, `At most ${MAX_DUPLICATE_SCORE}`);

export const DuplicateThresholds = z
  .object({ possible: threshold, moderate: threshold, strong: threshold, veryStrong: threshold })
  .superRefine((t, ctx) => {
    const order = ['possible', 'moderate', 'strong', 'veryStrong'] as const;
    for (let i = 1; i < order.length; i++) {
      if (t[order[i]!] <= t[order[i - 1]!])
        ctx.addIssue({
          code: 'custom',
          path: [order[i]!],
          message: 'Must be higher than the level below',
        });
    }
  });
export type DuplicateThresholds = z.infer<typeof DuplicateThresholds>;

export const DEFAULT_DUPLICATE_THRESHOLDS: DuplicateThresholds = {
  possible: 40,
  moderate: 60,
  strong: 80,
  veryStrong: 95,
};

export function duplicateLevel(
  score: number | null,
  t: DuplicateThresholds,
): DuplicateLevel | null {
  if (score === null) return null;
  if (score >= t.veryStrong) return 'very_strong';
  if (score >= t.strong) return 'strong';
  if (score >= t.moderate) return 'moderate';
  if (score >= t.possible) return 'possible';
  return null;
}

export const duplicateSignalLabel = (code: string) =>
  (DUPLICATE_SIGNALS as Record<string, { label: string }>)[code]?.label ?? code;
